import React, { useState, useEffect } from 'react';
import { searchCustomerByMobile, getCustomerServicesWithCases, getClients, getClientSchemes, graylistCustomer, reactivateCustomer, createCustomerWithPolicy } from '../services/customer.service';
import SortableTable from '../components/CaseBooking/SortableTable';
import { useNavigate } from 'react-router-dom';
import { formatDateTime } from '../utils/dateUtils';
import { getCurrentPatronId } from '../utils/brandingUtils';
import { useRoleAccess } from '../hooks/useRoleAccess';
import { formatMoney } from '../utils/formatMoney';
import { useDialog } from '../contexts/DialogContext';

const MOBILE_REGEX = /^[+]?[1-9][0-9]{7,14}$/;

const CUSTOMER_COLUMNS = [
    { key: 'expand', label: '' },
    { key: 'name', label: 'Name' },
    { key: 'vehicle_registration', label: 'Vehicle Registration' },
    { key: 'policy_number', label: 'Policy Number' },
    { key: 'mobile_number', label: 'Mobile Number' },
    { key: 'email', label: 'Email' },
    { key: 'address', label: 'Address' },
    { key: 'city', label: 'City' },
    { key: 'state', label: 'State' },
    { key: 'pincode', label: 'Pincode' },
    { key: 'is_active', label: 'Active' },
];

const POLICY_TYPE_OPTIONS = [
    { value: 'roadside_assistance', label: 'Roadside Assistance' },
    { value: 'core', label: 'RSA Core' },
    { value: 'max', label: 'RSA Max' },
    { value: 'comprehensive', label: 'Comprehensive' },
    { value: 'third_party', label: 'Third Party' },
    { value: 'fire_and_theft', label: 'Fire & Theft' }
];

const getDefaultPolicyDates = () => {
    const start = new Date();
    const end = new Date();
    end.setFullYear(end.getFullYear() + 1);

    const toInputDate = (date) => date.toISOString().split('T')[0];

    return {
        start_date: toInputDate(start),
        end_date: toInputDate(end)
    };
};

const createInitialCustomerForm = () => {
    const { start_date, end_date } = getDefaultPolicyDates();
    return {
        clientId: '',
        schemeId: '',
        customer: {
            name: '',
            email: '',
            address: '',
            city: '',
            state: '',
            pincode: '',
            date_of_birth: ''
        },
        vehicle: {
            registration_number: '',
            make: '',
            model: '',
            year: '',
            color: '',
            engine_number: '',
            chassis_number: '',
            fuel_type: ''
        },
        policy: {
            policy_number: '',
            scheme_policy_number: '',
            policy_type: 'roadside_assistance',
            start_date,
            end_date,
            premium_amount: '',
            coverage_amount: '',
            max_kms: '',
            max_claims_count: '',
            is_active: true
        },
        is_paid_customer: false
    };
};

const formatDate = (date) => formatDateTime(date);

const getStatusBadge = (status) => {
    const statusClasses = {
        open: 'bg-yellow-100 text-yellow-800',
        in_progress: 'bg-blue-100 text-blue-800',
        resolved: 'bg-green-100 text-green-800',
        closed: 'bg-gray-100 text-gray-800',
        cancelled: 'bg-red-100 text-red-800',
        pending: 'bg-yellow-100 text-yellow-800',
        assigned: 'bg-blue-100 text-blue-800',
        driver_started: 'bg-purple-100 text-purple-800',
        vendor_reached_location: 'bg-indigo-100 text-indigo-800',
        service_in_progress: 'bg-orange-100 text-orange-800',
        service_complete: 'bg-green-100 text-green-800',
        vendor_not_accepted: 'bg-red-100 text-red-800'
    };

    return (
        <span className={`px-2 inline-block text-xs leading-5 font-semibold capitalize w-100 text-center rounded-md py-1 ${statusClasses[status] || 'bg-gray-100 text-gray-800'}`}>
            {status ? status.replace(/_/g, ' ').toUpperCase() : 'N/A'}
        </span>
    );
};

const CaseBooking = () => {
    const currentPatronId = getCurrentPatronId();
    // Check if current patron is SDA to conditionally show/hide fields
    const isCurrentPatronSDA = currentPatronId === 'sda';
    const isCurrentPatronSFA = currentPatronId === 'sfa';

    // Get user role and permissions
    const { isAdmin, isManager, isAgent } = useRoleAccess();
    const { toast, confirm } = useDialog();

    const [formData, setFormData] = useState({
        name: '',
        phone: '',
        chassisNumber: '',
        policyNumber: '',
        vehicleRegistration: '',
        clientId: '',
        schemeId: ''
    });
    const [error, setError] = useState('');
    const [result, setResult] = useState(null);
    const [expandedRowId, setExpandedRowId] = useState(null);
    const [customerServices, setCustomerServices] = useState({});
    const [loading, setLoading] = useState(false);
    const [servicesLoading, setServicesLoading] = useState(false);
    const [clients, setClients] = useState([]);
    const [schemes, setSchemes] = useState([]);
    const [loadingClients, setLoadingClients] = useState(true);
    const [loadingSchemes, setLoadingSchemes] = useState(false);
    const [actionLoading, setActionLoading] = useState({});
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [createForm, setCreateForm] = useState(() => createInitialCustomerForm());
    const [createError, setCreateError] = useState('');
    const [createLoading, setCreateLoading] = useState(false);
    const navigate = useNavigate();
    // Allow creating customer if we have phone and clientId
    // schemeId is only required for non-paid customers (handled in modal)
    // Show create customer button if user is SFA and has phone number
    // Client and scheme will be selected in the modal
    const canCreateCustomer = isCurrentPatronSFA && Boolean(formData.phone);

    // Fetch clients on component mount
    useEffect(() => {
        const fetchClients = async () => {
            setLoadingClients(true);
            try {
                const response = await getClients({ is_active: true });
                setClients(response || []);

                // Auto-select if only one client
                if (response && response.length === 1) {
                    setFormData(prev => ({ ...prev, clientId: response[0].id }));
                    // Fetch schemes for the single client
                    fetchSchemes(response[0].id);
                }
            } catch (error) {
                console.error('Error fetching clients:', error);
                setClients([]);
            } finally {
                setLoadingClients(false);
            }
        };
        fetchClients();
    }, []);

    // Fetch schemes when client is selected
    const fetchSchemes = async (clientId) => {
        if (!clientId) {
            setSchemes([]);
            return;
        }

        setLoadingSchemes(true);
        try {
            const response = await getClientSchemes(clientId, { is_active: true });
            setSchemes(response || []);

            // Auto-select if only one scheme
            if (response && response.length === 1) {
                setFormData(prev => ({ ...prev, schemeId: response[0].id }));
            }
        } catch (error) {
            console.error('Error fetching schemes:', error);
            setSchemes([]);
        } finally {
            setLoadingSchemes(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === "phone") {
            // Allow any phone number format as provided by user
            setFormData(prev => ({
                ...prev,
                phone: value
            }));
        } else if (name === "clientId") {
            // Handle client selection
            setFormData(prev => ({
                ...prev,
                clientId: value,
                schemeId: '' // Reset scheme when client changes
            }));
            // Fetch schemes for selected client
            if (value) {
                fetchSchemes(value);
            } else {
                setSchemes([]);
            }
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
        setError('');
    };

    const validateSearchParameters = () => {
        const { phone, policyNumber, vehicleRegistration } = formData;

        // Check if at least one search parameter is provided
        if (!phone && !policyNumber && !vehicleRegistration) {
            setError('Please provide at least one search parameter: Mobile Number, Policy Number, or Vehicle Registration Number.');
            return false;
        }

        // Validate mobile number if provided
        if (phone && !MOBILE_REGEX.test(phone)) {
            setError('Please enter a valid mobile number (e.g. +919876543210).');
            return false;
        }

        return true;
    };

    const buildSearchPayload = () => {
        const payload = {};
        if (formData.clientId) {
            payload.client_id = Number(formData.clientId);
        }
        if (formData.schemeId) {
            payload.client_scheme_id = Number(formData.schemeId);
        }
        if (formData.phone) {
            payload.mobile_number = formData.phone.trim();
        }
        if (formData.policyNumber) {
            payload.policy_number = formData.policyNumber.trim();
        }
        if (formData.vehicleRegistration) {
            payload.vehicle_registration_number = formData.vehicleRegistration.trim().toUpperCase();
        }
        return payload;
    };

    const performSearch = async () => {
        const searchPayload = buildSearchPayload();
        const data = await searchCustomerByMobile(searchPayload);
        setResult(data);
        return data;
    };

    const resetCreateForm = () => {
        setCreateForm(createInitialCustomerForm());
    };

    const openCreateModal = () => {
        if (!formData.phone) {
            setError('Please provide mobile number before creating a customer.');
            return;
        }
        // Initialize form with pre-filled values from search
        const defaultForm = createInitialCustomerForm();
        // Pre-fill client and scheme if available from search
        if (formData.clientId) {
            defaultForm.clientId = formData.clientId;
            // Fetch schemes for the client if available
            if (formData.clientId) {
                fetchSchemes(formData.clientId);
            }
        }
        if (formData.schemeId) {
            defaultForm.schemeId = formData.schemeId;
        }
        if (formData.vehicleRegistration) {
            defaultForm.vehicle.registration_number = formData.vehicleRegistration.trim().toUpperCase();
        }
        if (formData.policyNumber) {
            defaultForm.policy.policy_number = formData.policyNumber.trim().toUpperCase();
        }
        setCreateForm(defaultForm);
        setCreateError('');
        setShowCreateModal(true);
    };

    const closeCreateModal = () => {
        setShowCreateModal(false);
        setCreateError('');
        setCreateLoading(false);
    };

    const handleCreateCustomerFieldChange = (e) => {
        const { name, value } = e.target;
        setCreateForm(prev => ({
            ...prev,
            customer: {
                ...prev.customer,
                [name]: value
            }
        }));
    };

    const handleCreateVehicleFieldChange = (e) => {
        const { name, value } = e.target;
        setCreateForm(prev => ({
            ...prev,
            vehicle: {
                ...prev.vehicle,
                [name]: value
            }
        }));
    };

    const handleCreatePolicyFieldChange = (e) => {
        const { name, value, type, checked } = e.target;
        setCreateForm(prev => ({
            ...prev,
            policy: {
                ...prev.policy,
                [name]: type === 'checkbox' ? checked : value
            }
        }));
    };

    const handleCreateClientChange = (e) => {
        const clientId = e.target.value;
        setCreateForm(prev => ({
            ...prev,
            clientId: clientId,
            schemeId: '' // Reset scheme when client changes
        }));
        // Fetch schemes for selected client
        if (clientId) {
            fetchSchemes(clientId);
        } else {
            setSchemes([]);
        }
    };

    const handleCreateSchemeChange = (e) => {
        const schemeId = e.target.value;
        setCreateForm(prev => ({
            ...prev,
            schemeId: schemeId
        }));
    };

    const sanitizeNumber = (value) => {
        if (value === '' || value === null || value === undefined) {
            return undefined;
        }
        const parsed = Number(value);
        return Number.isNaN(parsed) ? undefined : parsed;
    };

    const handleCreateCustomerSubmit = async (e) => {
        e.preventDefault();
        setCreateError('');

        if (!formData.phone) {
            setCreateError('Mobile number is required.');
            return;
        }

        if (!createForm.clientId) {
            setCreateError('Client selection is required.');
            return;
        }

        // Scheme is only required for non-paid customers (when creating a policy)
        if (!createForm.is_paid_customer && !createForm.schemeId) {
            setCreateError('Scheme selection is required when creating a customer with policy.');
            return;
        }

        if (!createForm.customer.name.trim()) {
            setCreateError('Customer name is required.');
            return;
        }

        if (!createForm.vehicle.registration_number.trim()) {
            setCreateError('Vehicle registration number is required.');
            return;
        }

        if (!createForm.vehicle.make.trim() || !createForm.vehicle.model.trim()) {
            setCreateError('Vehicle make and model are required.');
            return;
        }

        if (!createForm.is_paid_customer) {
            // For non-paid customers, we need schemeId
            if (!createForm.schemeId) {
                setCreateError('Scheme selection is required when creating a customer with policy.');
                return;
            }

            if (!createForm.policy.policy_number.trim()) {
                setCreateError('Policy number is required.');
                return;
            }

            if (!createForm.policy.start_date || !createForm.policy.end_date) {
                setCreateError('Policy start and end dates are required.');
                return;
            }

            if (new Date(createForm.policy.end_date) <= new Date(createForm.policy.start_date)) {
                setCreateError('Policy end date must be after the start date.');
                return;
            }
        }

        const vehiclePayload = {
            registration_number: createForm.vehicle.registration_number.trim().toUpperCase(),
            make: createForm.vehicle.make.trim(),
            model: createForm.vehicle.model.trim()
        };

        const vehicleYear = sanitizeNumber(createForm.vehicle.year);
        if (vehicleYear !== undefined) vehiclePayload.year = vehicleYear;
        if (createForm.vehicle.color) vehiclePayload.color = createForm.vehicle.color.trim();
        if (createForm.vehicle.engine_number) vehiclePayload.engine_number = createForm.vehicle.engine_number.trim();
        if (createForm.vehicle.chassis_number) vehiclePayload.chassis_number = createForm.vehicle.chassis_number.trim();
        if (createForm.vehicle.fuel_type) vehiclePayload.fuel_type = createForm.vehicle.fuel_type.trim();

        const policyPayload = {
            policy_number: createForm.policy.policy_number.trim().toUpperCase(),
            client_scheme_id: Number(createForm.schemeId),
            policy_type: createForm.policy.policy_type,
            start_date: createForm.policy.start_date,
            end_date: createForm.policy.end_date,
            is_active: !!createForm.policy.is_active
        };

        if (createForm.policy.scheme_policy_number) {
            policyPayload.scheme_policy_number = createForm.policy.scheme_policy_number.trim().toUpperCase();
        }
        const premium = sanitizeNumber(createForm.policy.premium_amount);
        if (premium !== undefined) policyPayload.premium_amount = premium;
        const coverage = sanitizeNumber(createForm.policy.coverage_amount);
        if (coverage !== undefined) policyPayload.coverage_amount = coverage;
        const maxKms = sanitizeNumber(createForm.policy.max_kms);
        if (maxKms !== undefined) policyPayload.max_kms = maxKms;
        const maxClaims = sanitizeNumber(createForm.policy.max_claims_count);
        if (maxClaims !== undefined) policyPayload.max_claims_count = maxClaims;

        const payload = {
            name: createForm.customer.name.trim(),
            mobile_number: formData.phone.trim(),
            email: createForm.customer.email?.trim() || undefined,
            address: createForm.customer.address?.trim() || undefined,
            city: createForm.customer.city?.trim() || undefined,
            state: createForm.customer.state?.trim() || undefined,
            pincode: createForm.customer.pincode?.trim() || undefined,
            date_of_birth: createForm.customer.date_of_birth || undefined,
            vehicle: vehiclePayload,
            client_id: Number(createForm.clientId),
            is_paid_customer: createForm.is_paid_customer
        };

        if (!createForm.is_paid_customer) {
            payload.policy = policyPayload;
        }

        try {
            setCreateLoading(true);
            await createCustomerWithPolicy(payload);
            closeCreateModal();
            resetCreateForm();
            toast.success('Customer created successfully.');
            setLoading(true);
            try {
                await performSearch();
            } catch (searchError) {
                console.error('Error refreshing results:', searchError);
                setError(searchError.message || 'Customer created, but unable to refresh the results.');
            } finally {
                setLoading(false);
            }
        } catch (err) {
            const errMessage = err?.response?.data?.detail || err.message || 'Failed to create customer.';
            setCreateError(errMessage);
        } finally {
            setCreateLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setResult(null);
        setCustomerServices({});
        setExpandedRowId(null);
        setError('');

        // Validate search parameters
        if (!validateSearchParameters()) {
            return;
        }

        // client_id and scheme_id are optional - can search by unique fields without them

        setLoading(true);
        try {
            await performSearch();
        } catch (err) {
            console.error('Search error:', err);
            setError(err.message || 'An error occurred while searching. Please check your connection and try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleExpand = async (rowId, customerId) => {
        if (expandedRowId === rowId) {
            setExpandedRowId(null);
            return;
        }

        if (customerServices[customerId]) {
            setExpandedRowId(rowId);
            return;
        }

        setServicesLoading(true);
        try {
            const servicesData = await getCustomerServicesWithCases(customerId);
            setCustomerServices(prev => ({
                ...prev,
                [customerId]: servicesData
            }));
            setExpandedRowId(rowId);
        } catch (err) {
            console.error('Error fetching services:', err);
            // Don't show error to user, just log it
        } finally {
            setServicesLoading(false);
        }
    };

    const handleClear = () => {
        // Clear search parameters but preserve auto-selected client/scheme
        const clearData = {
            name: '',
            phone: '',
            chassisNumber: '',
            policyNumber: '',
            vehicleRegistration: '',
            clientId: formData.clientId, // Preserve current client
            schemeId: formData.schemeId  // Preserve current scheme
        };

        setFormData(clearData);
        setError('');
        setResult(null);
        setCustomerServices({});
        setExpandedRowId(null);
    };

    const handleGraylistCustomer = async (customerId, customerName) => {
        const confirmed = await confirm({
            title: 'Graylist customer',
            message: `Are you sure you want to graylist ${customerName}?`,
            confirmLabel: 'Graylist',
        });

        if (!confirmed) return; // User cancelled

        setActionLoading(prev => ({ ...prev, [`graylist-${customerId}`]: true }));

        try {
            await graylistCustomer(customerId);

            // Update the customer status in the result
            setResult(prev => ({
                ...prev,
                data: prev.data.map(record => ({
                    ...record,
                    customer: record.customer.id === customerId
                        ? { ...record.customer, status: 'graylisted' }
                        : record.customer
                }))
            }));

            toast.success('Customer graylisted successfully');
        } catch (error) {
            console.error('Error graylisting customer:', error);
            toast.error(error.message || 'Failed to graylist customer');
        } finally {
            setActionLoading(prev => ({ ...prev, [`graylist-${customerId}`]: false }));
        }
    };

    const handleReactivateCustomer = async (customerId, customerName) => {
        const confirmed = await confirm({
            title: 'Reactivate customer',
            message: `Are you sure you want to reactivate ${customerName}?`,
            confirmLabel: 'Reactivate',
            danger: false,
        });

        if (!confirmed) return; // User cancelled

        setActionLoading(prev => ({ ...prev, [`reactivate-${customerId}`]: true }));

        try {
            await reactivateCustomer(customerId);

            // Update the customer status in the result
            setResult(prev => ({
                ...prev,
                data: prev.data.map(record => ({
                    ...record,
                    customer: record.customer.id === customerId
                        ? { ...record.customer, status: 'active' }
                        : record.customer
                }))
            }));

            toast.success('Customer reactivated successfully');
        } catch (error) {
            console.error('Error reactivating customer:', error);
            toast.error(error.message || 'Failed to reactivate customer');
        } finally {
            setActionLoading(prev => ({ ...prev, [`reactivate-${customerId}`]: false }));
        }
    };

    const renderCustomerTable = () => {
        if (!result) return null;
        if (!result.found) {
            return (
                <div className="mt-4">
                    <div className="bg-white rounded-lg shadow p-6">
                        <div className="text-center py-8 space-y-4">
                            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                            <div>
                                <h3 className="mt-2 text-lg font-medium text-gray-900">Customer Not Found</h3>
                                <p className="mt-1 text-sm text-gray-500">
                                    No customer found with the provided search criteria. Please check the details and try again.
                                </p>
                            </div>
                            {canCreateCustomer && (
                                <div className="pt-2">
                                    <button
                                        type="button"
                                        onClick={openCreateModal}
                                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                    >
                                        Create Customer
                                    </button>
                                    <p className="mt-2 text-xs text-gray-500">
                                        Use the details you entered to create a new customer with their policy and vehicle information.
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            );
        }

        // Handle new response structure
        const customerRecords = result.data || [];

        return (
            <div className="mt-8">
                <div className="bg-white rounded-lg p-2 border-none">
                    <h2 className="text-xl font-semibold mb-4">Customer Information</h2>
                    <div className="w-full overflow-x-auto">
                        <table className="min-w-[1400px] divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    {CUSTOMER_COLUMNS.map(col => (
                                        <th key={col.key} className="px-6 py-3 text-left text-1x1 font-medium text-black-500  whitespace-nowrap">
                                            {col.label}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {customerRecords.map((record, index) => {
                                    const customer = record.customer;
                                    const rowId = `${customer.id}-${index}`;
                                    return (
                                        <React.Fragment key={rowId}>
                                            <tr className="hover:bg-gray-50">
                                                <td className="px-6 py-4 whitespace-nowrap text-gray-600">
                                                    <button
                                                        onClick={() => handleExpand(rowId, customer.id)}
                                                        className="text-gray-400 hover:text-gray-600 focus:outline-none"
                                                    >
                                                        <svg
                                                            className={`w-5 h-5 transform transition-transform ${expandedRowId === rowId ? 'rotate-90' : ''}`}
                                                            fill="none"
                                                            stroke="currentColor"
                                                            viewBox="0 0 24 24"
                                                        >
                                                            <path
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                                strokeWidth={2}
                                                                d="M9 5l7 7-7 7"
                                                            />
                                                        </svg>
                                                    </button>
                                                </td>
                                                <td className="px-6 py-4 text-gray-600 whitespace-nowrap">{customer.name}</td>
                                                <td className="px-6 py-4 text-gray-600 whitespace-nowrap">{record.vehicle?.registration_number || 'N/A'}</td>
                                                <td className="px-6 py-4 text-gray-600 whitespace-nowrap">{record.policy?.policy_number || 'N/A'}</td>
                                                <td className="px-6 py-4 text-gray-600 whitespace-nowrap">{customer.mobile_number}</td>
                                                <td className="px-6 py-4 text-gray-600 whitespace-nowrap">{customer.email || 'N/A'}</td>
                                                <td className="px-6 py-4 text-gray-600 whitespace-nowrap">{customer.address || 'N/A'}</td>
                                                <td className="px-6 py-4 text-gray-600 whitespace-nowrap">{customer.city || 'N/A'}</td>
                                                <td className="px-6 py-4 text-gray-600 whitespace-nowrap">{customer.state || 'N/A'}</td>
                                                <td className="px-6 py-4 text-gray-600 whitespace-nowrap">{customer.pincode || 'N/A'}</td>
                                                <td className="px-6 py-4 text-gray-600 whitespace-nowrap">{customer.status}</td>
                                            </tr>
                                            {expandedRowId === rowId && (
                                                <tr>
                                                    <td colSpan={CUSTOMER_COLUMNS.length} className="px-6 py-4 bg-gray-50">
                                                        <div className="pl-6">
                                                            <div className="mb-4 flex items-center gap-3">
                                                                {/* Create Case Button */}
                                                                {customer.status === 'graylisted' ? (
                                                                    <div className="flex items-center gap-2">
                                                                        <button
                                                                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-gray-400 cursor-not-allowed"
                                                                            disabled
                                                                        >
                                                                            Create Case
                                                                        </button>
                                                                        <div className="flex items-center text-gray-500">
                                                                            <svg className="w-5 h-5 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                                                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                                                            </svg>
                                                                            <span className="text-sm">Customer is graylisted</span>
                                                                        </div>
                                                                    </div>
                                                                ) : (
                                                                    <button
                                                                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                                                        onClick={() => {
                                                                            // Policy is mandatory unless it's a paid customer
                                                                            if (!record.policy?.id && !customer.is_paid_customer) {
                                                                                toast.error('Policy information is missing. Cannot create case.');
                                                                                return;
                                                                            }
                                                                            if (!record.vehicle?.id) {
                                                                                toast.error('Vehicle information is missing. Cannot create case.');
                                                                                return;
                                                                            }
                                                                            navigate('/create-case', {
                                                                                state: {
                                                                                    customer: {
                                                                                        ...customer,
                                                                                        client_id: formData.clientId || record.client_scheme?.client_id,
                                                                                        scheme_id: formData.schemeId || record.client_scheme?.id,
                                                                                        policy_id: record.policy?.id,
                                                                                        vehicle_id: record.vehicle.id
                                                                                    }
                                                                                }
                                                                            });
                                                                        }}
                                                                    >
                                                                        Create Case
                                                                    </button>
                                                                )}

                                                                {/* Graylist Button - Agent or higher */}
                                                                {(isAgent || isManager || isAdmin) && customer.status !== 'graylisted' && customer.status !== 'blocked' && (
                                                                    <button
                                                                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-yellow-600 hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 disabled:opacity-50"
                                                                        onClick={() => handleGraylistCustomer(customer.id, customer.name)}
                                                                        disabled={actionLoading[`graylist-${customer.id}`]}
                                                                    >
                                                                        {actionLoading[`graylist-${customer.id}`] ? 'Graylisting...' : 'Graylist'}
                                                                    </button>
                                                                )}

                                                                {/* Reactivate Button - Manager or higher, only for graylisted customers */}
                                                                {(isManager || isAdmin) && customer.status === 'graylisted' && (
                                                                    <button
                                                                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
                                                                        onClick={() => handleReactivateCustomer(customer.id, customer.name)}
                                                                        disabled={actionLoading[`reactivate-${customer.id}`]}
                                                                    >
                                                                        {actionLoading[`reactivate-${customer.id}`] ? 'Reactivating...' : 'Reactivate'}
                                                                    </button>
                                                                )}
                                                            </div>

                                                            <div className="mb-4">
                                                                <h3 className="text-lg font-medium text-gray-900">Customer Services</h3>
                                                            </div>

                                                            {servicesLoading ? (
                                                                <p className="text-gray-500">Loading services...</p>
                                                            ) : (
                                                                <div className="w-full overflow-x-auto">
                                                                    <SortableTable
                                                                        columns={[
                                                                            {
                                                                                key: 'service_number',
                                                                                label: 'Service Number',
                                                                                render: (item) => (
                                                                                    <button
                                                                                        onClick={() => navigate('/add-service', {
                                                                                            state: {
                                                                                                case_id: item.case_id,
                                                                                                service_id: item.service_id
                                                                                            }
                                                                                        })}
                                                                                        className="text-blue-600 hover:text-blue-800 underline cursor-pointer"
                                                                                    >
                                                                                        {item.service_number}
                                                                                    </button>
                                                                                )
                                                                            },
                                                                            { key: 'service_status', label: 'Service Status', render: (item) => getStatusBadge(item.service_status) },
                                                                            { key: 'service_type', label: 'Service Type' },
                                                                            { key: 'case_number', label: 'Case Number' },
                                                                            { key: 'case_status', label: 'Case Status', render: (item) => getStatusBadge(item.case_status) },
                                                                            { key: 'service_description', label: 'Description' },
                                                                            { key: 'vehicle_registration', label: 'Vehicle Registration' },
                                                                            { key: 'vendor_name', label: 'Vendor', render: (item) => item.vendor_name || 'N/A' },
                                                                            { key: 'driver_name', label: 'Driver', render: (item) => item.driver_name || 'N/A' },
                                                                            { key: 'estimated_cost', label: 'Estimated Cost', render: (item) => item.estimated_cost ? formatMoney(item.estimated_cost) : 'N/A' },
                                                                            { key: 'service_created_at', label: 'Created At', render: (item) => formatDate(item.service_created_at) },
                                                                            { key: 'completed_at', label: 'CompletedAt', render: (item) => formatDate(item.completed_at) },
                                                                        ]}
                                                                        data={(customerServices[customer.id]?.services || []).filter(service => service.service_status !== 'service_complete')}
                                                                        emptyMessage={
                                                                            <div className="flex flex-col items-start text-gray-400 h-24">
                                                                                <svg className="h-8 w-8 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                                                </svg>
                                                                                <span>No active services available</span>
                                                                            </div>
                                                                        }
                                                                        containerClassName="w-full overflow-x-auto  rounded-md"
                                                                        className="min-w-[1800px] border"
                                                                    />
                                                                </div>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        );
    };

    const renderCreateCustomerModal = () => {
        if (!showCreateModal) return null;

        return (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 px-4">
                <div className="bg-white rounded-lg shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-y-auto p-6">
                    <div className="flex items-start justify-between mb-4">
                        <div>
                            <h3 className="text-2xl font-semibold text-gray-900">Create Customer</h3>
                            <p className="text-sm text-gray-500">Enter the customer, vehicle, and policy details to create a new record.</p>
                        </div>
                        <button
                            type="button"
                            onClick={closeCreateModal}
                            className="text-gray-400 hover:text-gray-600"
                        >
                            <span className="sr-only">Close modal</span>
                            ✕
                        </button>
                    </div>

                    <div className="bg-gray-50 rounded-lg p-4 mb-6 text-sm">
                        <p className="text-gray-500 mb-2">Mobile Number</p>
                        <p className="font-medium text-gray-900">{formData.phone || 'N/A'}</p>
                    </div>

                    <form onSubmit={handleCreateCustomerSubmit} className="space-y-8">
                        {/* Client and Scheme Selection */}
                        <section>
                            <h4 className="text-lg font-semibold mb-4">Client & Scheme Selection</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Client Name <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        name="clientId"
                                        value={createForm.clientId}
                                        onChange={handleCreateClientChange}
                                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        required
                                    >
                                        <option value="">- Select Client -</option>
                                        {loadingClients ? (
                                            <option>Loading...</option>
                                        ) : (
                                            clients.map(client => (
                                                <option key={client.id} value={client.id}>
                                                    {client.client_name}
                                                </option>
                                            ))
                                        )}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Scheme Name {!createForm.is_paid_customer && <span className="text-red-500">*</span>}
                                        {createForm.is_paid_customer && <span className="text-xs text-gray-400">(Optional for paid customers)</span>}
                                    </label>
                                    <select
                                        name="schemeId"
                                        value={createForm.schemeId}
                                        onChange={handleCreateSchemeChange}
                                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        disabled={!createForm.clientId}
                                        required={!createForm.is_paid_customer}
                                    >
                                        <option value="">- Select Scheme -</option>
                                        {loadingSchemes ? (
                                            <option>Loading...</option>
                                        ) : (
                                            schemes.map(scheme => (
                                                <option key={scheme.id} value={scheme.id}>
                                                    {scheme.scheme_name}
                                                </option>
                                            ))
                                        )}
                                    </select>
                                </div>
                            </div>
                        </section>
                        <div className="flex items-center space-x-2 bg-blue-50 p-3 rounded-md">
                            <input
                                type="checkbox"
                                id="is_paid_customer"
                                checked={createForm.is_paid_customer}
                                onChange={(e) => setCreateForm(prev => ({ ...prev, is_paid_customer: e.target.checked }))}
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                            <label htmlFor="is_paid_customer" className="text-sm font-medium text-gray-900">
                                Paid Customer (Policy details not required)
                            </label>
                        </div>
                        <section>
                            <h4 className="text-lg font-semibold mb-4">Customer Details</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Name <span className="text-red-500">*</span></label>
                                    <input
                                        type="text"
                                        name="name"
                                        value={createForm.customer.name}
                                        onChange={handleCreateCustomerFieldChange}
                                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        placeholder="Customer name"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                                    <input
                                        type="email"
                                        name="email"
                                        value={createForm.customer.email}
                                        onChange={handleCreateCustomerFieldChange}
                                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        placeholder="customer@email.com"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth</label>
                                    <input
                                        type="date"
                                        name="date_of_birth"
                                        value={createForm.customer.date_of_birth}
                                        onChange={handleCreateCustomerFieldChange}
                                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Pincode</label>
                                    <input
                                        type="text"
                                        name="pincode"
                                        value={createForm.customer.pincode}
                                        onChange={handleCreateCustomerFieldChange}
                                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        placeholder="e.g. 560001"
                                    />
                                </div>
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                                    <textarea
                                        name="address"
                                        value={createForm.customer.address}
                                        onChange={handleCreateCustomerFieldChange}
                                        rows={2}
                                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        placeholder="Street, area, and other address details"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                                    <input
                                        type="text"
                                        name="city"
                                        value={createForm.customer.city}
                                        onChange={handleCreateCustomerFieldChange}
                                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                                    <input
                                        type="text"
                                        name="state"
                                        value={createForm.customer.state}
                                        onChange={handleCreateCustomerFieldChange}
                                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                </div>
                            </div>
                        </section>

                        <section className="pt-4 border-t border-gray-200">
                            <h4 className="text-lg font-semibold mb-4">Vehicle Details</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Registration Number <span className="text-red-500">*</span></label>
                                    <input
                                        type="text"
                                        name="registration_number"
                                        value={createForm.vehicle.registration_number}
                                        onChange={handleCreateVehicleFieldChange}
                                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 uppercase"
                                        placeholder="KA01AB1234"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Make <span className="text-red-500">*</span></label>
                                    <input
                                        type="text"
                                        name="make"
                                        value={createForm.vehicle.make}
                                        onChange={handleCreateVehicleFieldChange}
                                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        placeholder="Vehicle make"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Model <span className="text-red-500">*</span></label>
                                    <input
                                        type="text"
                                        name="model"
                                        value={createForm.vehicle.model}
                                        onChange={handleCreateVehicleFieldChange}
                                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        placeholder="Vehicle model"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
                                    <input
                                        type="number"
                                        name="year"
                                        value={createForm.vehicle.year}
                                        onChange={handleCreateVehicleFieldChange}
                                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        placeholder="2024"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Color</label>
                                    <input
                                        type="text"
                                        name="color"
                                        value={createForm.vehicle.color}
                                        onChange={handleCreateVehicleFieldChange}
                                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Fuel Type</label>
                                    <input
                                        type="text"
                                        name="fuel_type"
                                        value={createForm.vehicle.fuel_type}
                                        onChange={handleCreateVehicleFieldChange}
                                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        placeholder="Petrol / Diesel / EV"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Engine Number</label>
                                    <input
                                        type="text"
                                        name="engine_number"
                                        value={createForm.vehicle.engine_number}
                                        onChange={handleCreateVehicleFieldChange}
                                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Chassis Number</label>
                                    <input
                                        type="text"
                                        name="chassis_number"
                                        value={createForm.vehicle.chassis_number}
                                        onChange={handleCreateVehicleFieldChange}
                                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                </div>
                            </div>
                        </section>

                        {!createForm.is_paid_customer && (
                            <section className="pt-4 border-t border-gray-200">
                                <h4 className="text-lg font-semibold mb-4">Policy Details</h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Policy Number <span className="text-red-500">*</span></label>
                                        <input
                                            type="text"
                                            name="policy_number"
                                            value={createForm.policy.policy_number}
                                            onChange={handleCreatePolicyFieldChange}
                                            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 uppercase"
                                            placeholder="Policy number"
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Scheme Policy Number</label>
                                        <input
                                            type="text"
                                            name="scheme_policy_number"
                                            value={createForm.policy.scheme_policy_number}
                                            onChange={handleCreatePolicyFieldChange}
                                            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 uppercase"
                                            placeholder="Client-specific policy no."
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Policy Type</label>
                                        <select
                                            name="policy_type"
                                            value={createForm.policy.policy_type}
                                            onChange={handleCreatePolicyFieldChange}
                                            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        >
                                            {POLICY_TYPE_OPTIONS.map(option => (
                                                <option key={option.value} value={option.value}>
                                                    {option.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:col-span-1">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Start Date <span className="text-red-500">*</span></label>
                                            <input
                                                type="date"
                                                name="start_date"
                                                value={createForm.policy.start_date}
                                                onChange={handleCreatePolicyFieldChange}
                                                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                required
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">End Date <span className="text-red-500">*</span></label>
                                            <input
                                                type="date"
                                                name="end_date"
                                                value={createForm.policy.end_date}
                                                onChange={handleCreatePolicyFieldChange}
                                                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                required
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Premium Amount</label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            name="premium_amount"
                                            value={createForm.policy.premium_amount}
                                            onChange={handleCreatePolicyFieldChange}
                                            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            placeholder="e.g. 1499"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Coverage Amount</label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            name="coverage_amount"
                                            value={createForm.policy.coverage_amount}
                                            onChange={handleCreatePolicyFieldChange}
                                            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            placeholder="e.g. 50000"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Max KMs</label>
                                        <input
                                            type="number"
                                            step="0.1"
                                            name="max_kms"
                                            value={createForm.policy.max_kms}
                                            onChange={handleCreatePolicyFieldChange}
                                            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            placeholder="Distance allowance"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Max Claims</label>
                                        <input
                                            type="number"
                                            name="max_claims_count"
                                            value={createForm.policy.max_claims_count}
                                            onChange={handleCreatePolicyFieldChange}
                                            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            placeholder="Claims allowed"
                                        />
                                    </div>
                                    <div className="md:col-span-2 flex items-center space-x-2">
                                        <input
                                            type="checkbox"
                                            id="policy_is_active"
                                            name="is_active"
                                            checked={createForm.policy.is_active}
                                            onChange={handleCreatePolicyFieldChange}
                                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                        />
                                        <label htmlFor="policy_is_active" className="text-sm text-gray-700">Policy is active</label>
                                    </div>
                                </div>
                            </section>
                        )}

                        {createError && (
                            <div className="text-red-600 text-sm font-medium">{createError}</div>
                        )}

                        <div className="flex justify-end gap-3">
                            <button
                                type="button"
                                onClick={closeCreateModal}
                                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={createLoading}
                                className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-60"
                            >
                                {createLoading ? 'Creating...' : 'Create Customer'}
                            </button>
                        </div>
                    </form>
                </div>
            </div >
        );
    };

    return (
        <div className="space-y-6 mb-4">
            <h1 className="text-2xl font-semibold text-gray-900 px-3">Case Booking</h1>
            <div className="bg-white rounded-lg bxshadowhd p-6 mrgall border">
                <form onSubmit={handleSubmit} className="space-y-6 p-0 border-b">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {/* Name */}
                        <div>
                            <label className="block text-sm font-normal text-black mb-1">
                                Name
                            </label>
                            <input
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                className="outline-none w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-100"
                                placeholder="Enter customer name"
                                autoComplete="off"
                                disabled
                            />
                        </div>
                        {/* Phone */}
                        <div>
                            <label className="block text-sm font-normal text-black mb-1">
                                Phone
                            </label>
                            <input
                                type="text"
                                name="phone"
                                value={formData.phone}
                                onChange={handleChange}
                                className="outline-none w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                placeholder="Enter phone number" maxLength="13"
                                autoComplete="off"
                            />
                        </div>
                        {/* Chassis Number */}
                        <div>
                            <label className="block text-sm font-normal text-black mb-1">
                                Chassis Number
                            </label>
                            <input
                                type="text"
                                name="chassisNumber"
                                value={formData.chassisNumber}
                                onChange={handleChange}
                                className="outline-none w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-100"
                                placeholder="Enter chassis number"
                                autoComplete="off"
                                disabled
                            />
                        </div>
                        {/* Policy/LAN Number */}
                        <div>
                            <label className="block text-sm font-normal text-black mb-1">
                                Policy/LAN Number
                            </label>
                            <input
                                type="text"
                                name="policyNumber"
                                value={formData.policyNumber}
                                onChange={handleChange}
                                className="outline-none w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                placeholder="Enter policy/LAN number"
                                autoComplete="off"
                            />
                        </div>
                        {/* Vehicle Registration */}
                        <div>
                            <label className="block text-sm font-normal text-black mb-1">
                                Vehicle Registration No
                            </label>
                            <input
                                type="text"
                                name="vehicleRegistration"
                                value={formData.vehicleRegistration}
                                onChange={handleChange}
                                className="outline-none w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                placeholder="Enter vehicle registration"
                                autoComplete="off"
                            />
                        </div>
                        {/* Client Name - Only show if not SDA patron */}
                        {!isCurrentPatronSDA && (
                            <div className='regit_bform'>
                                <label className="block text-sm font-normal text-black mb-1">
                                    Client Name
                                </label>
                                <select
                                    name="clientId"
                                    value={formData.clientId}
                                    onChange={handleChange}
                                    className="outline-none w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                >
                                    <option value="">- Select Client -</option>
                                    {loadingClients ? (
                                        <option>Loading...</option>
                                    ) : (
                                        clients.map(client => (
                                            <option key={client.id} value={client.id}>
                                                {client.client_name}
                                            </option>
                                        ))
                                    )}
                                </select>
                            </div>
                        )}
                        {/* Scheme Name - Only show if not SDA patron */}
                        {!isCurrentPatronSDA && (
                            <div className='regit_bform '>
                                <label className="block text-sm font-normal text-black mb-1">
                                    Scheme Name
                                </label>
                                <select
                                    name="schemeId"
                                    value={formData.schemeId}
                                    onChange={handleChange}
                                    className="outline-none w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    disabled={!formData.clientId}
                                >
                                    <option value="">- Select Scheme -</option>
                                    {loadingSchemes ? (
                                        <option>Loading...</option>
                                    ) : (
                                        schemes.map(scheme => (
                                            <option key={scheme.id} value={scheme.id}>
                                                {scheme.scheme_name}
                                            </option>
                                        ))
                                    )}
                                </select>
                            </div>
                        )}
                    </div>
                    {error && (
                        <div className="text-red-600 text-sm font-medium mt-2">{error}</div>
                    )}
                    <div className="flex justify-center gap-4 pb-5">
                        <button
                            type="button"
                            onClick={handleClear}
                            className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                            Clear
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                        >
                            {loading ? 'Searching...' : 'Search'}
                        </button>
                    </div>
                </form>
                {renderCustomerTable()}
            </div>
            {renderCreateCustomerModal()}
        </div>
    );
};

export default CaseBooking; 